import { useCallback, useEffect, useRef, useState } from 'react';
import { msalInstance } from '@/config/msalConfig';

const IDLE_TIMEOUT_MS = 25 * 60 * 1000; // 25 minutes total (20 min idle + 5 min warning)
const WARNING_DURATION_MS = 5 * 60 * 1000; // warning shows at 20 min, logout fires at 25 min

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'] as const;

function decrementCountdown(prev: number): number {
  return Math.max(0, prev - 1);
}

export interface IdleTimeoutState {
  /** True when the session-expiry warning modal should be visible. */
  showWarning: boolean;
  /** Seconds remaining until automatic logout (meaningful only when showWarning is true). */
  secondsRemaining: number;
  /** Call this when the user chooses to extend their session. Resets the idle timer. */
  stayLoggedIn: () => void;
}

/**
 * Monitors user activity and:
 *  - Shows a warning (showWarning=true) `warningMs` before the session expires.
 *  - Triggers an MSAL logout redirect after `timeoutMs` of inactivity.
 *  - Any tracked activity event or a call to stayLoggedIn() resets the countdown.
 *
 * Uses the msalInstance singleton directly — no context subscription,
 * so this causes zero re-renders from auth state changes.
 */
export function useIdleTimeout(
  timeoutMs: number = IDLE_TIMEOUT_MS,
  warningMs: number = WARNING_DURATION_MS,
): IdleTimeoutState {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(warningMs / 1000);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref mirror of showWarning — lets the activity handler read the current value
  // without a stale closure, so activity events are ignored while the modal is open.
  const showWarningRef = useRef(false);

  // Exposed as stayLoggedIn — also used internally as the activity/visibility reset handler.
  const stayLoggedIn = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    showWarningRef.current = false;
    setShowWarning(false);
    setSecondsRemaining(warningMs / 1000);

    warningTimerRef.current = setTimeout(() => {
      showWarningRef.current = true;
      setShowWarning(true);
      setSecondsRemaining(warningMs / 1000);
      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining(decrementCountdown);
      }, 1_000);
    }, timeoutMs - warningMs);

    logoutTimerRef.current = setTimeout(() => {
      void msalInstance.logoutRedirect();
    }, timeoutMs);
  }, [timeoutMs, warningMs]);

  useEffect(() => {
    // Only reset the timer when the warning modal is NOT showing.
    // Once the modal is visible the user must click a button — not just move the mouse.
    const handleActivity = () => {
      if (!showWarningRef.current) stayLoggedIn();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !showWarningRef.current) stayLoggedIn();
    };

    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivity));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    stayLoggedIn(); // start timer immediately on mount

    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [stayLoggedIn]);

  return { showWarning, secondsRemaining, stayLoggedIn };
}
